import AssignmentIcon from '@mui/icons-material/Assignment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CompareIcon from '@mui/icons-material/Compare';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FolderIcon from '@mui/icons-material/Folder';
import HistoryIcon from '@mui/icons-material/History';
import LockIcon from '@mui/icons-material/Lock';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MovieCreationIcon from '@mui/icons-material/MovieCreation';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import RefreshIcon from '@mui/icons-material/Refresh';
import SplitScreenIcon from '@mui/icons-material/Splitscreen';
import TimelineIcon from '@mui/icons-material/Timeline';
import TranslateIcon from '@mui/icons-material/Translate';
import VideoSettingsIcon from '@mui/icons-material/VideoSettings';
import {
    Backdrop,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAssignmentContext } from '../../contexts/AssignmentContext';
import audioFileServiceInstance from '../../services/AudioFileService';
import fileSystemAccessServiceInstance from '../../services/FileSystemAccessService';
import idmlServiceInstance from '../../services/IdmlService';
import textFileServiceInstance from '../../services/TextFileService';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import videoDownloadServiceInstance from '../../services/VideoDownloadService';
import videoUploadServiceInstance from '../../services/VideoUploadService';
import { getVibrantIconColor } from '../../themes/VibrantTheme';
import { getUserType, isAdmin, isDubber, isGlossaryManager } from '../../utils/authUtils';
import { LinkTypes } from '../../utils/constants';
import { validateItemName } from '../../utils/formatUtils';
import { htmlToDocxBlob, htmlToHtmlContent, htmlToPdfBlob, htmlToPlainText } from '../../utils/textFileDownloadUtils';
import AssignVideoDialog from '../dialogs/AssignVideoDialog';
import AudioFileDetailsDialog from '../dialogs/AudioFileDetailsDialog';
import BookFileDetailsDialog from '../dialogs/BookFileDetailsDialog';
import BulkDownloadDialog from '../dialogs/BulkDownloadDialog';
import DubbingTransformDialog from '../dialogs/DubbingTransformDialog';
import FolderHistoryDialog from '../dialogs/FolderHistoryDialog';
import FolderPermissionsDialog from '../dialogs/FolderPermissionsDialog';

import MoveDialog from '../dialogs/MoveDialog';
import PathSelectorDialog from '../dialogs/PathSelectorDialog';
import ProgressStagesDialog from '../dialogs/ProgressStagesDialog';
import SectionManagerDialog from '../dialogs/SectionManagerDialog';
import Text2TextDialog from '../dialogs/Text2TextDialog';
import TextFileDetailsDialog from '../dialogs/TextFileDetailsDialog';
import VideoHistoryDialog from '../dialogs/VideoHistoryDialog';
import VoiceOverDialog from '../dialogs/VoiceOverDialog';
import { flatLangOptions, getLanguagesForCreateVideo } from '../subtitles/SelectLanguageComponent';
import SplitVideoDialog from '../subtitles/SplitVideoDialog';
import UploadVideoDialog from '../subtitles/UploadVideoDialog';
import TaskTrackingService from '../../services/TaskTrackingService';
import { startTrackingForNewContent } from '../tasks/startTracking';
import ManageTaskDialog from '../tasks/ManageTaskDialog';
import StartTrackingDialog from '../tasks/StartTrackingDialog';
import TaskDrawer from '../tasks/TaskDrawer';
import { TaskIndicatorsContext } from '../tasks/TaskIndicators';
import { indexTasksByContent, savesSrt, trashTasksNote, videoCanOpen, videoNotReadyMessage } from '../tasks/taskUi';
import useTaskSummary, { seesTasks } from '../tasks/useTaskSummary';
import ModernFolderView from './ModernFolderView';

const FolderBrowser = ({ setCanNavigateToEdit, darkMode, toggleDarkMode, themeVariant }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // State variables
    const [selectedFile, setSelectedFile] = useState('');
    const [folders, setFolders] = useState([]);
    const [videoFiles, setVideoFiles] = useState([]);
    const [textFiles, setTextFiles] = useState([]);
    const [audioFiles, setAudioFiles] = useState([]);
    const [bookFiles, setBookFiles] = useState([]);
    // Task indicators on language chips (only for people who see tasks)
    const taskSummary = useTaskSummary();
    // AV works from Posting calendar (6j-6). Show in files from a task of theirs still lands here.
    useEffect(() => {
        if (taskSummary?.avOnly && !location.state?.navigateToFolder) navigate('/tasks', { replace: true });
    }, [taskSummary, location.state, navigate]);
    const [taskIndex, setTaskIndex] = useState({});
    const [taskRefresh, setTaskRefresh] = useState(0);
    const [drawerTaskId, setDrawerTaskId] = useState(null);
    // Manage task from a file's three dots menu; kept here because closing the menu clears selectedItem
    const [trackFiles, setTrackFiles] = useState([]);
    const [trackDialogOpen, setTrackDialogOpen] = useState(false);
    const [manageTaskOpen, setManageTaskOpen] = useState(false);
    const [file, setFile] = useState('');
    const [videoName, setVideoName] = useState('');
    const [open, setOpen] = useState(false);
    const [fileUploading, setFileUploading] = useState(false);
    const [linkError, setLinkError] = useState(false);
    const [isCompressed, setIsCompressed] = useState(false);
    const [isLogging, setIsLogging] = useState(false);
    const [selectOpenAI, setSelectOpenAI] = useState(true);
    const [bitRate, setBitRate] = useState('115');
    const [crfValue, setCRFValue] = useState('28');
    const [skipTranslationIsChecked, setSkipTranslationIsChecked] = useState(false);
    const [onlyTransliterationIsChecked, setOnlyTransliterationIsChecked] = useState(false);
    const [currentPath, setCurrentPath] = useState('/');
    const [linkType, setLinkType] = useState(LinkTypes.NONE);
    const [youtubeLinkURL, setYoutubeLinkURL] = useState('');
    const [googleLinkURL, setGoogleLinkURL] = useState('');
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [text2TextDialogOpen, setText2TextDialogOpen] = useState(false);
    const [selectedVideoForText2Text, setSelectedVideoForText2Text] = useState(null);
    const [progressStagesDialogOpen, setProgressStagesDialogOpen] = useState(false);
    const [selectedVideoForProgressStages, setSelectedVideoForProgressStages] = useState(null);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [dubbingTransformationDialogOpen, setDubbingTransformationDialogOpen] = useState(false);
    const [selectedVideoForHistory, setSelectedVideoForHistory] = useState(null);
    const [selectedVideoForDubbingTransformation, setSelectedVideoForDubbingTransformation] = useState(null);
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [itemToMove, setItemToMove] = useState(null);
    const [assignVideoDialogOpen, setAssignVideoDialogOpen] = useState(false);
    const [selectedVideoForAssign, setSelectedVideoForAssign] = useState(null);
    const [folderHistoryDialogOpen, setFolderHistoryDialogOpen] = useState(false);
    const [selectedFolderForHistory, setSelectedFolderForHistory] = useState(null);
    const [bulkDownloadDialogOpen, setBulkDownloadDialogOpen] = useState(false);
    const [selectedFolderForBulkDownload, setSelectedFolderForBulkDownload] = useState(null);
    const [audioFileDetailsDialogOpen, setAudioFileDetailsDialogOpen] = useState(false);
    const [selectedAudioFileDetails, setSelectedAudioFileDetails] = useState(null);
    const [textFileDetailsDialogOpen, setTextFileDetailsDialogOpen] = useState(false);
    const [selectedTextFileDetails, setSelectedTextFileDetails] = useState(null);
    const [bookFileDetailsDialogOpen, setBookFileDetailsDialogOpen] = useState(false);
    const [selectedBookFileDetails, setSelectedBookFileDetails] = useState(null);

    // Split Video dialog state
    const [splitVideoOpen, setSplitVideoOpen] = useState(false);
    const [splitVideoPath, setSplitVideoPath] = useState('/');

    // Text file translate dialog
    const [textFileTranslateDialogOpen, setTextFileTranslateDialogOpen] = useState(false);
    const [textFileTranslateTarget, setTextFileTranslateTarget] = useState('');
    const [textFileTranslating, setTextFileTranslating] = useState(false);
    const [textFileTranslateFileIds, setTextFileTranslateFileIds] = useState(new Set());

    // Download text files by language dialog
    const [downloadByLangDialogOpen, setDownloadByLangDialogOpen] = useState(false);
    const [downloadByLangLanguages, setDownloadByLangLanguages] = useState(new Set());
    const [downloadByLangFormats, setDownloadByLangFormats] = useState(new Set(['docx']));
    const [downloadByLangLoading, setDownloadByLangLoading] = useState(false);
    const [downloadByLangFileIds, setDownloadByLangFileIds] = useState(new Set());

    // Add these state variables to track changes
    const [refreshFlag, setRefreshFlag] = useState(0);
    const [stagesDialogChangesMade, setStagesDialogChangesMade] = useState(false);
    const [historyDialogChangesMade, setHistoryDialogChangesMade] = useState(false);

    // Add state for folder creation dialog
    const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [folderDestinationPath, setFolderDestinationPath] = useState('/');
    const [folderPathDialogOpen, setFolderPathDialogOpen] = useState(false);

    // Add this new state for section manager dialog
    const [sectionManagerDialogOpen, setSectionManagerDialogOpen] = useState(false);
    const [selectedFileForSections, setSelectedFileForSections] = useState(null);

    // Add state for VO script dialog
    const [voiceOverDialogOpen, setVoiceOverDialogOpen] = useState(false);
    const [selectedVideoForVoiceOver, setSelectedVideoForVoiceOver] = useState(null);

    // Add state for permissions dialog
    const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
    const [selectedFolderForPermissions, setSelectedFolderForPermissions] = useState(null);

    // Store user role for easy access throughout the component
    const [userType, setUserType] = useState('');

    // Use the assignment context for any assignment operations
    const { assignVideo } = useAssignmentContext();

    // Initialize user type on component mount
    useEffect(() => {
        setUserType(getUserType());
    }, []);

    useEffect(() => {
        // Check if coming from Glossary Editor breadcrumb navigation
        if (location.state?.navigateToFolder) {
            const folderPath = location.state.navigateToFolder;
            setCurrentPath(folderPath);
            loadFolderContents(folderPath);
            return;
        }

        // Check if we're coming from the video edit page
        if (location.state?.fromVideoEdit) {
            // Check if a refresh is needed based on the breadcrumb navigation return
            const needsRefresh = location.state?.refreshNeeded || false;

            if (location.state?.folderPath) {
                const folderPath = location.state.folderPath;
                setCurrentPath(folderPath);
                loadFolderContents(folderPath);
            } else {
                const storedBreadcrumbs = localStorage.getItem('breadcrumbs');

                if (storedBreadcrumbs) {
                    try {
                        const breadcrumbArray = JSON.parse(storedBreadcrumbs);
                        if (Array.isArray(breadcrumbArray) && breadcrumbArray.length > 0) {
                            const folderPath = '/' + breadcrumbArray.join('/');
                            setCurrentPath(folderPath);
                            // Only refresh if explicitly needed or we don't yet know if changes were made
                            if (needsRefresh) {
                                loadFolderContents(folderPath);
                            } else {
                                // Just set the path, but don't reload
                                loadFolderContents(folderPath);
                            }
                            return;
                        }
                    } catch (err) {
                        console.error('Error parsing breadcrumbs:', err);
                    }
                }

                // Fallback to root if no breadcrumbs found
                setCurrentPath('/');
                loadFolderContents('/');
            }
        } else {
            loadFolderContents('/');
        }
    }, [location]);

    // Add a dependency-based effect for refreshing when flags change
    useEffect(() => {
        // Only refresh if explicitly needed via flags
        if (stagesDialogChangesMade || historyDialogChangesMade) {
            loadFolderContents(currentPath);
            // Reset the flags after refresh
            setStagesDialogChangesMade(false);
            setHistoryDialogChangesMade(false);
        }
    }, [refreshFlag, stagesDialogChangesMade, historyDialogChangesMade]);

    const handleShowPermissionsDialog = (item) => {
        setSelectedFolderForPermissions(item);
        setPermissionsDialogOpen(true);
    };

    const fetchTextFiles = (fileList) => {
        if (!fileList || fileList.length === 0) {
            setFileUploading(false);
            return;
        }

        // Get all text file IDs
        const textFileIds = fileList.map((file) => file.data).filter(Boolean);

        if (textFileIds.length === 0) {
            setFileUploading(false);
            return;
        }

        setFileUploading(true);

        // Create promises that won't reject the whole chain
        const safePromises = textFileIds.map((id) =>
            textFileServiceInstance
                .getTextFileById(id)
                .then((data) => ({
                    success: true,
                    id: id,
                    data: data,
                }))
                .catch((error) => ({
                    success: false,
                    id: id,
                    error: error,
                })),
        );

        Promise.all(safePromises)
            .then((results) => {
                // Log errors but continue processing successful responses
                results.forEach((result) => {
                    if (!result.success) {
                        console.warn(`Failed to fetch data for text file ID ${result.id}:`, result.error);
                    }
                });

                // Filter out failed requests and process only successful ones
                const successfulResponses = results.filter((result) => result.success);

                if (successfulResponses.length === 0) {
                    setFileUploading(false);
                    return;
                }

                // Process successful responses
                const textFileData = successfulResponses.map((result) => {
                    const fileData = result.data;

                    console.log('TOO - Fetched text file data:', fileData);
                    return {
                        data: fileData.id,
                        id: fileData.id,
                        name: fileData.name || '',
                        type: fileData.type || 'text',
                        content: fileData.content || '',
                        updatedBy: fileData.updatedBy || '',
                        updatedAt: fileData.updatedAt || '',
                        createdBy: fileData.createdBy || '',
                        createdAt: fileData.createdAt || '',
                        translations: fileData.translations || {},
                    };
                });

                // Update text files state
                setTextFiles(textFileData);
                setFileUploading(false);
            })
            .catch((error) => {
                console.error('Error fetching text files:', error);
                setFileUploading(false);
            });
    };

    const fetchBookFiles = (fileList) => {
        if (!fileList || fileList.length === 0) {
            return;
        }

        const bookIds = fileList.map((file) => file.data).filter(Boolean);
        if (bookIds.length === 0) {
            return;
        }

        const safePromises = bookIds.map((id) =>
            idmlServiceInstance
                .getBook(id)
                .then((response) => ({ success: true, id, data: response.book }))
                .catch((error) => ({ success: false, id, error })),
        );

        Promise.all(safePromises)
            .then((results) => {
                results.forEach((result) => {
                    if (!result.success) {
                        console.warn(`Failed to fetch data for book id ${result.id}:`, result.error);
                    }
                });

                const successfulResponses = results.filter((result) => result.success);
                if (successfulResponses.length === 0) {
                    return;
                }

                const bookFileData = fileList.map((file) => {
                    const matching = successfulResponses.find((r) => r.id === file.data);
                    if (!matching) return file;
                    const book = matching.data;
                    return {
                        ...file,
                        id: book.id,
                        name: book.name || file.name,
                        sourceLanguage: book.sourceLanguage || '',
                        targetLanguages: book.targetLanguages || [],
                        targetLanguageStatus: book.targetLanguageStatus || {},
                        processStage: book.processStage || 'UPLOADED',
                        updatedBy: book.updatedBy || file.updatedBy || '',
                        updatedAt: book.updatedAt || file.updatedAt || '',
                    };
                });

                setBookFiles(bookFileData);
            })
            .catch((error) => {
                console.error('Unexpected error in fetchBookFiles:', error);
            });
    };

    const fetchFileLanguages = (fileList) => {
        if (!fileList || fileList.length === 0) return;

        // Get all video IDs
        const videoIds = fileList.map((file) => file.data).filter(Boolean);

        if (videoIds.length === 0) return;

        setFileUploading(true);

        // Create promises that won't reject the whole chain
        const safePromises = videoIds.map((id) =>
            videoCRUDServiceInstance
                .getVideoById(id)
                .then((response) => ({
                    success: true,
                    id: id,
                    data: response.data,
                }))
                .catch((error) => ({
                    success: false,
                    id: id,
                    error: error,
                })),
        );

        Promise.all(safePromises)
            .then((results) => {
                // Log errors but continue processing successful responses
                results.forEach((result) => {
                    if (!result.success) {
                        console.warn(`Failed to fetch data for video ID ${result.id}:`, result.error);
                    }
                });

                // Filter out failed requests and process only successful ones
                const successfulResponses = results.filter((result) => result.success);

                // Process successful responses
                const languageData = successfulResponses.map((result) => {
                    const videoData = result.data;

                    const languagesWithStatus = videoData.videoLanguages
                        ? videoData.videoLanguages.map((lang) => ({
                              name: lang.languageName,
                              status: lang.status,
                              vttFileUrl: lang.vttFileUrl || '',
                              processStage: lang.processStage || 'UNKNOWN',
                          }))
                        : [];

                    return {
                        id: videoData.id,
                        videoUrl: videoData.videoUrl || '',
                        type: videoData.type || 'video',
                        videoLanguages: videoData.videoLanguages || [],
                        languages: languagesWithStatus,
                        videoName: videoData.videoName || '',
                        compressedUrl: videoData.compressedUrl || '',
                        updatedBy: videoData.updatedBy || '',
                        updatedAt: videoData.lastModifiedDate || '',
                        duration: videoData.duration || '',
                        processingStatus: videoData.processingStatus || '',
                        workFileEnv: videoData.workFileEnv || '',
                    };
                });

                // Update files with video data
                const updatedFiles = fileList.map((file) => {
                    const videoId = file.data;
                    const matchingData = languageData.find((item) => item.id === videoId);

                    if (matchingData) {
                        return {
                            ...file,
                            id: matchingData.id,
                            languages: matchingData.languages || [],
                            videoLanguages: matchingData.videoLanguages || [],
                            videoName: matchingData.videoName || file.name || '',
                            compressedUrl: matchingData.compressedUrl || '',
                            updatedBy: matchingData.updatedBy || '',
                            updatedAt: matchingData.updatedAt || '',
                            duration: matchingData.duration || '',
                            videoUrl: matchingData.videoUrl || '',
                            processingStatus: matchingData.processingStatus,
                            workFileEnv: matchingData.workFileEnv,
                        };
                    }

                    return file;
                });

                setVideoFiles(updatedFiles);
            })
            .catch((error) => {
                // This shouldn't happen since we wrapped each promise to prevent rejection
                console.error('Unexpected error in fetchFileLanguages:', error);
            })
            .finally(() => {
                setFileUploading(false);
            });
    };

    const loadFolderContents = useCallback(
        (path) => {
            const userData = JSON.parse(localStorage.getItem('authUser'));
            if (!userData) {
                console.error('No user data found in localStorage');
                return;
            }

            setFileUploading(true);

            fileSystemAccessServiceInstance
                .getFolderContents(path, userData.email, userData.access)
                .then((response) => {
                    const folderItems = response.data.filter((e) => e.isFolder === true);
                    const fileItems = response.data.filter((e) => e.isFolder === false);
                    const videoItems = fileItems.filter((item) => item.type === 'video');
                    const textItems = fileItems.filter((item) => item.type === 'text');
                    const audioItems = fileItems.filter((item) => item.type === 'audio');
                    const bookItems = fileItems.filter((item) => item.type === 'book');

                    setFolders(folderItems);
                    setVideoFiles(videoItems);
                    setTextFiles(textItems);
                    setAudioFiles(audioItems);
                    setBookFiles(bookItems);

                    // Fetch languages for all files
                    if (videoItems.length > 0) {
                        fetchFileLanguages(videoItems);
                    } else {
                        setFileUploading(false);
                    }

                    if (textItems.length > 0) {
                        fetchTextFiles(textItems);
                    } else {
                        setFileUploading(false);
                    }

                    if (bookItems.length > 0) {
                        fetchBookFiles(bookItems);
                    }
                })
                .catch((e) => {
                    console.error('Error loading folder contents:', e);
                    setFileUploading(false);
                });
        },
        [fetchFileLanguages],
    );

    const handleFolderClick = useCallback(
        (folder) => {
            if (folder.path === undefined || folder.path === '') {
                folder.path = currentPath === '/' ? currentPath + folder.name : currentPath + '/' + folder.name;
            }
            setCurrentPath(folder.path);
            loadFolderContents(folder.path);
        },
        [currentPath, loadFolderContents],
    );

    const handleVideoFileClick = useCallback(
        async (fileData) => {
            localStorage.removeItem('breadcrumbs');
            localStorage.removeItem('videoProps');
            localStorage.removeItem('subtitle');
            localStorage.removeItem('timelineProps');
            localStorage.removeItem('checkedForDelete');
            localStorage.removeItem('lang');

            setFileUploading(true);

            try {
                const userData = JSON.parse(localStorage.getItem('authUser'));

                let videoId = fileData.data;

                if (!videoId) {
                    console.log('Error could not determine video ID');
                    return;
                }

                const response = await videoCRUDServiceInstance.getVideoById(videoId, userData.email, userData.access);

                const fullVideoData = response.data;

                localStorage.setItem('videoProps', JSON.stringify(fullVideoData));

                const breadcrumbs = currentPath.split('/').filter(Boolean);
                localStorage.setItem('breadcrumbs', JSON.stringify(breadcrumbs));

                setFileUploading(false);

                if (videoCanOpen(fullVideoData)) {
                    setCanNavigateToEdit(true);
                    navigate('/videos/edit-v2', { state: { videoData: fullVideoData } });
                } else {
                    setCanNavigateToEdit(false);
                    Swal.fire({ ...videoNotReadyMessage(fullVideoData), confirmButtonText: 'Close' });
                    return;
                }
            } catch (error) {
                console.error('Error fetching video details:', error);
                setFileUploading(false);

                Swal.fire({
                    title: 'Error',
                    text: 'Failed to load video details. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'Close',
                });
            }
        },
        [currentPath, navigate, setCanNavigateToEdit],
    );

    // Legacy route: opens a video in the V1 subtitle editor from the context menu
    const handleOpenEditorV1 = useCallback(
        async (fileData) => {
            localStorage.removeItem('breadcrumbs');
            localStorage.removeItem('videoProps');
            localStorage.removeItem('subtitle');
            localStorage.removeItem('timelineProps');
            localStorage.removeItem('checkedForDelete');
            localStorage.removeItem('lang');

            setFileUploading(true);

            try {
                const userData = JSON.parse(localStorage.getItem('authUser'));
                const videoId = fileData.data;

                if (!videoId) {
                    Swal.fire('Error', 'Could not determine video ID', 'error');
                    return;
                }

                const response = await videoCRUDServiceInstance.getVideoById(videoId, userData.email, userData.access);
                const fullVideoData = response.data;

                localStorage.setItem('videoProps', JSON.stringify(fullVideoData));

                const breadcrumbs = currentPath.split('/').filter(Boolean);
                localStorage.setItem('breadcrumbs', JSON.stringify(breadcrumbs));

                setFileUploading(false);

                if (videoCanOpen(fullVideoData)) {
                    setCanNavigateToEdit(true);
                    navigate('/videos/edit', { state: { videoData: fullVideoData } });
                } else {
                    setCanNavigateToEdit(false);
                    Swal.fire({ ...videoNotReadyMessage(fullVideoData), confirmButtonText: 'Close' });
                }
            } catch (error) {
                console.error('Error opening Editor V1:', error);
                setFileUploading(false);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to load video details. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'Close',
                });
            }
        },
        [currentPath, navigate, setCanNavigateToEdit],
    );

    const handleSrtSearchResultClick = useCallback(
        async (result) => {
            localStorage.removeItem('breadcrumbs');
            localStorage.removeItem('videoProps');
            localStorage.removeItem('subtitle');
            localStorage.removeItem('timelineProps');
            localStorage.removeItem('checkedForDelete');
            localStorage.removeItem('lang');

            setFileUploading(true);

            try {
                const userData = JSON.parse(localStorage.getItem('authUser'));
                const response = await videoCRUDServiceInstance.getVideoById(result.videoId, userData.email, userData.access);
                const fullVideoData = response.data;

                localStorage.setItem('videoProps', JSON.stringify(fullVideoData));
                const breadcrumbs = currentPath.split('/').filter(Boolean);
                localStorage.setItem('breadcrumbs', JSON.stringify(breadcrumbs));

                setFileUploading(false);

                if (fullVideoData.videoLanguages) {
                    setCanNavigateToEdit(true);
                    navigate('/videos/edit-v2', { state: { videoData: fullVideoData, seekToTime: result.startTime, selectedLanguage: result.languageName } });
                } else {
                    setCanNavigateToEdit(false);
                    Swal.fire({ title: 'Video not ready', text: 'The video is not ready for editing yet.', icon: 'warning', confirmButtonText: 'Close' });
                }
            } catch (error) {
                console.error('Error opening Editor V2 from SRT search:', error);
                setFileUploading(false);
                Swal.fire({ title: 'Error', text: 'Failed to load video details. Please try again.', icon: 'error', confirmButtonText: 'Close' });
            }
        },
        [currentPath, navigate, setCanNavigateToEdit],
    );

    const handleOpenVideoEditor = useCallback(
        async (fileData) => {
            setFileUploading(true);
            try {
                const userData = JSON.parse(localStorage.getItem('authUser'));
                const videoId = fileData.data;

                if (!videoId) {
                    Swal.fire('Error', 'Could not determine video ID', 'error');
                    return;
                }

                const response = await videoCRUDServiceInstance.getVideoById(videoId, userData.email, userData.access);
                const fullVideoData = response.data;

                localStorage.setItem('videoEditorProps', JSON.stringify(fullVideoData));

                // Save breadcrumbs so VideoEditorPage can show the folder path
                const breadcrumbs = currentPath.split('/').filter(Boolean);
                localStorage.setItem('breadcrumbs', JSON.stringify(breadcrumbs));

                setFileUploading(false);
                navigate(`/videos/video-editor/${videoId}`, { state: { videoData: fullVideoData } });
            } catch (error) {
                console.error('Error opening Video Editor:', error);
                setFileUploading(false);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to load video details. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'Close',
                });
            }
        },
        [currentPath, navigate],
    );

    const handleTextFileClick = useCallback(
        (fileData) => {
            console.log('TOO - Text File Click', fileData);

            // Get the text file ID
            const textFileId = fileData.data;

            if (!textFileId) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Could not determine text file ID',
                });
                return;
            }

            // Store the breadcrumbs for navigation back
            const breadcrumbs = currentPath.split('/').filter(Boolean);
            localStorage.setItem('breadcrumbs', JSON.stringify(breadcrumbs));

            // Navigate to the text editor with the file ID
            navigate(`/textfiles/edit/${textFileId}`);
        },
        [currentPath, navigate],
    );

    const handleBookFileClick = useCallback(
        (fileData) => {
            const bookId = fileData.data;

            if (!bookId) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Could not determine book ID',
                });
                return;
            }

            const breadcrumbs = currentPath.split('/').filter(Boolean);
            localStorage.setItem('breadcrumbs', JSON.stringify(breadcrumbs));

            navigate(`/idml/books/edit/${bookId}`);
        },
        [currentPath, navigate],
    );

    const handleShowAudioFileDetails = useCallback(async (fileData) => {
        if (!fileData || !fileData.data) {
            Swal.fire('Error', 'Could not determine audio file ID', 'error');
            return;
        }

        try {
            setFileUploading(true);
            const details = await audioFileServiceInstance.getAudioFileById(fileData.data);
            setSelectedAudioFileDetails(details || fileData);
            setAudioFileDetailsDialogOpen(true);
        } catch (e) {
            console.error('Failed to fetch audio file details:', e);
            Swal.fire('Error', 'Failed to load audio file details.', 'error');
        } finally {
            setFileUploading(false);
        }
    }, []);

    const handleShowTextFileDetails = useCallback(async (fileData) => {
        if (!fileData || !fileData.data) {
            Swal.fire('Error', 'Could not determine text file ID', 'error');
            return;
        }

        try {
            setFileUploading(true);
            const details = await textFileServiceInstance.getTextFileById(fileData.data);
            setSelectedTextFileDetails(details || fileData);
            setTextFileDetailsDialogOpen(true);
        } catch (e) {
            console.error('Failed to fetch text file details:', e);
            Swal.fire('Error', 'Failed to load text file details.', 'error');
        } finally {
            setFileUploading(false);
        }
    }, []);

    const handleShowBookFileDetails = useCallback(async (fileData) => {
        if (!fileData || !fileData.data) {
            Swal.fire('Error', 'Could not determine book ID', 'error');
            return;
        }

        try {
            setFileUploading(true);
            const response = await idmlServiceInstance.getBook(fileData.data);
            setSelectedBookFileDetails(response?.book || fileData);
            setBookFileDetailsDialogOpen(true);
        } catch (e) {
            console.error('Failed to fetch book details:', e);
            Swal.fire('Error', 'Failed to load book details.', 'error');
        } finally {
            setFileUploading(false);
        }
    }, []);

    const handleTextFileTranslate = useCallback(() => {
        setTextFileTranslateFileIds(new Set());
        setTextFileTranslateTarget('');
        setTextFileTranslateDialogOpen(true);
    }, []);

    const handleTextFileTranslateSubmit = useCallback(async () => {
        if (!textFileTranslateTarget || textFileTranslateFileIds.size === 0) return;
        setTextFileTranslating(true);
        try {
            let failCount = 0;
            for (const fileId of textFileTranslateFileIds) {
                try {
                    const details = await textFileServiceInstance.getTextFileById(fileId);
                    await textFileServiceInstance.startTranslation(fileId, details.sourceLanguage, [textFileTranslateTarget]);
                } catch (e) {
                    console.error('Failed to translate file:', fileId, e);
                    failCount++;
                }
            }
            setTextFileTranslateDialogOpen(false);
            if (failCount > 0) {
                Swal.fire('Partial Success', `${textFileTranslateFileIds.size - failCount} translation(s) started, ${failCount} failed.`, 'warning');
            } else {
                Swal.fire({ title: `${textFileTranslateFileIds.size} translation(s) started`, icon: 'success', timer: 2000, showConfirmButton: false });
            }
            loadFolderContents(currentPath);
        } catch (e) {
            console.error('Failed to start translation:', e);
            Swal.fire('Error', 'Failed to start translation: ' + (e.response?.data?.error || e.message), 'error');
        } finally {
            setTextFileTranslating(false);
        }
    }, [textFileTranslateFileIds, textFileTranslateTarget, currentPath]);

    const handleDownloadByLanguage = useCallback(async () => {
        if (downloadByLangLanguages.size === 0 || downloadByLangFormats.size === 0) return;
        setDownloadByLangLoading(true);
        try {
            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();
            let fileCount = 0;
            const filesToDownload = downloadByLangFileIds.size > 0
                ? textFiles.filter((tf) => downloadByLangFileIds.has(tf.data))
                : textFiles;

            for (const tf of filesToDownload) {
                try {
                    const details = await textFileServiceInstance.getTextFileById(tf.data);
                    const baseName = tf.name?.replace(/\.[^.]+$/, '') || tf.data;
                    for (const lang of downloadByLangLanguages) {
                        const translation = details.translations?.[lang];
                        if (!translation?.translatedTextUrl) continue;
                        const rawContent = await textFileServiceInstance.getTextFileContent(translation.translatedTextUrl);
                        if (!rawContent) continue;
                        for (const fmt of downloadByLangFormats) {
                            if (fmt === 'txt') {
                                zip.file(`${baseName}_${lang}.txt`, htmlToPlainText(rawContent));
                            } else if (fmt === 'pdf') {
                                const pdfBlob = await htmlToPdfBlob(rawContent);
                                zip.file(`${baseName}_${lang}.pdf`, pdfBlob);
                            } else if (fmt === 'docx') {
                                const docxBlob = await htmlToDocxBlob(rawContent);
                                zip.file(`${baseName}_${lang}.docx`, docxBlob);
                            } else {
                                zip.file(`${baseName}_${lang}.html`, htmlToHtmlContent(rawContent));
                            }
                            fileCount++;
                        }
                    }
                } catch (e) {
                    console.warn('Skipping file:', tf.name, e.message);
                }
            }
            if (fileCount === 0) {
                const langList = [...downloadByLangLanguages].join(', ');
                Swal.fire('No files', `No translations found for: ${langList}`, 'info');
                return;
            }
            const folderName = currentPath.split('/').filter(Boolean).pop() || 'files';
            const langSuffix = downloadByLangLanguages.size === 1 ? `_${[...downloadByLangLanguages][0]}` : '_multi-lang';
            const fmtSuffix = downloadByLangFormats.size === 1 ? `_${[...downloadByLangFormats][0]}` : '';
            const blob = await zip.generateAsync({ type: 'blob' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${folderName}${langSuffix}${fmtSuffix}.zip`;
            a.click();
            URL.revokeObjectURL(url);
            setDownloadByLangDialogOpen(false);
        } catch (e) {
            console.error('Download failed:', e);
            Swal.fire('Error', 'Download failed: ' + e.message, 'error');
        } finally {
            setDownloadByLangLoading(false);
        }
    }, [textFiles, downloadByLangFileIds, downloadByLangLanguages, downloadByLangFormats, currentPath]);

    const handleAudioFileClick = useCallback(
        async (fileData) => {
            await handleShowAudioFileDetails(fileData);
        },
        [handleShowAudioFileDetails],
    );

    const createFolder = async (folderName, path) => {
        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (userData) {
            return fileSystemAccessServiceInstance.createFolder(path, folderName, userData.email, userData.access);
        }
    };

    const [folderNameTouched, setFolderNameTouched] = useState(false);

    // Replace multiple separate state updates with batched updates
    const handleCreateFolder = useCallback(() => {
        // Add permission check for root directory
        if (currentPath === '/' && !isAdmin()) {
            Swal.fire({
                icon: 'error',
                title: 'Permission Denied',
                text: 'Only administrators can create folders in the root directory.',
            });
            return;
        }
        
        // Batch these state updates
        setNewFolderName('');
        setFolderDestinationPath(currentPath);
        setCreateFolderDialogOpen(true);
        setFolderNameTouched(false);
    }, [currentPath]);

    const handleCreateFolderSubmit = async () => {
        if (!newFolderName) return;

        try {
            setFileUploading(true); // Show loading indicator

            await createFolder(newFolderName, folderDestinationPath);

            // Explicitly close the dialog first
            setCreateFolderDialogOpen(false);

            // Wait a moment to ensure dialog is fully closed
            setTimeout(() => {
                // If folder was created in current path, just refresh
                if (folderDestinationPath === currentPath) {
                    loadFolderContents(currentPath);
                } else {
                    // If created in a different path, navigate to that path
                    setCurrentPath(folderDestinationPath);
                    loadFolderContents(folderDestinationPath);
                }

                setFileUploading(false); // Hide loading indicator

                // Show success message after dialog is closed
                Swal.fire({
                    icon: 'success',
                    title: 'Folder Created',
                    text: `Folder "${newFolderName}" created successfully`,
                    timer: 1500,
                    showConfirmButton: false,
                });
            }, 300);
        } catch (error) {
            setFileUploading(false);
            console.error('Error creating folder:', error);

            // Make sure dialog is closed even on error
            setCreateFolderDialogOpen(false);

            setTimeout(() => {
                Swal.fire('Error', 'Failed to create folder', 'error');
            }, 300);
        }
    };

    const handleShowFolderHistory = (folder) => {
        if (!folder) return;

        setSelectedFolderForHistory(folder);
        setFolderHistoryDialogOpen(true);
        handleMenuClose();
    };

    // Improved error handling in the handleRename function
    const handleRename = async (item, newName) => {
        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (!userData) return Promise.reject(new Error('No user data found'));

        try {
            // Handle based on whether it's a folder or file
            if (item.isFolder) {
                const itemPath = `${currentPath}/${item.name}`.replace(/\/\//g, '/');
                try {
                    await fileSystemAccessServiceInstance.renameFolder(
                        itemPath,
                        newName,
                        userData.email,
                        userData.access,
                    );

                    // Refresh folder contents and show success message
                    loadFolderContents(currentPath);
                    Swal.fire({
                        icon: 'success',
                        title: 'Renamed',
                        text: `"${item.name}" renamed to "${newName}" successfully`,
                        timer: 1500,
                        showConfirmButton: false,
                    });

                    return true;
                } catch (error) {
                    console.error('Rename folder error:', error);

                    // Handle specific error cases
                    if (error.response) {
                        // Handle 404 error explicitly
                        if (error.response.status === 404) {
                            Swal.fire({
                                icon: 'error',
                                title: 'Rename Error',
                                text: `Not able to rename, make sure there is no folder named "${newName}" already in this location.`,
                                confirmButtonText: 'OK',
                            });

                            // Refresh to show current state
                            loadFolderContents(currentPath);
                            return false;
                        }

                        // Handle folder already exists
                        if (
                            error.response.data &&
                            (error.response.data.errorCode === 'FOLDER_EXISTS' ||
                                error.response.data.message?.includes('already exists'))
                        ) {
                            Swal.fire({
                                icon: 'error',
                                title: 'Name Already Exists',
                                text: `A folder named "${newFolderName}" already exists in this location.`,
                                confirmButtonText: 'OK',
                            });
                            return false;
                        }
                    }

                    // Generic error handling
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed to rename folder',
                        text: error.response?.data?.message || error.message || 'An unknown error occurred',
                        confirmButtonText: 'OK',
                    });

                    return Promise.reject(error);
                }
            } else if (item.type === 'video') {
                // Video file renaming logic
                const videoId = item.data;
                if (!videoId) {
                    return Promise.reject(new Error('Could not determine video ID'));
                }

                try {
                    await videoCRUDServiceInstance.rename(
                        videoId,
                        currentPath,
                        userData.email,
                        userData.access,
                        newName,
                    );

                    loadFolderContents(currentPath);
                    Swal.fire({
                        icon: 'success',
                        title: 'Renamed',
                        text: `"${item.videoName || item.name}" renamed to "${newName}" successfully`,
                        timer: 1500,
                        showConfirmButton: false,
                    });

                    return true;
                } catch (error) {
                    console.error('Rename video error:', error);

                    // Handle specific error cases
                    if (error.response) {
                        // Handle 404 error explicitly
                        if (error.response.status === 404) {
                            Swal.fire({
                                icon: 'error',
                                title: 'Video Not Found',
                                text: `The video "${
                                    item.videoName || item.name
                                }" could not be found. It may have been deleted or moved.`,
                                confirmButtonText: 'OK',
                            });

                            // Refresh to show current state
                            loadFolderContents(currentPath);
                            return false;
                        }

                        // Handle video name already exists
                        if (
                            error.response.data &&
                            (error.response.data.errorCode === 'FILE_EXISTS' ||
                                error.response.data.message?.includes('already exists'))
                        ) {
                            Swal.fire({
                                icon: 'error',
                                title: 'Name Already Exists',
                                text: `A video named "${newFolderName}" already exists in this location.`,
                                confirmButtonText: 'OK',
                            });
                            return false;
                        }
                    }

                    // Generic error handling
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed to rename video',
                        text: error.response?.data?.message || error.message || 'An unknown error occurred',
                        confirmButtonText: 'OK',
                    });

                    return Promise.reject(error);
                }
            } else if (item.type === 'text') {
                const textId = item.data;
                if (!textId) {
                    return Promise.reject(new Error('Could not determine video ID'));
                }

                try {
                    await textFileServiceInstance.rename(textId, currentPath, newName);

                    loadFolderContents(currentPath);
                    Swal.fire({
                        icon: 'success',
                        title: 'Renamed',
                        text: `"${item.name}" renamed to "${newName}" successfully`,
                        timer: 1500,
                        showConfirmButton: false,
                    });

                    return true;
                } catch (error) {
                    console.error('Rename text error:', error);

                    // Handle specific error cases
                    if (error.response) {
                        // Handle 404 error explicitly
                        if (error.response.status === 404) {
                            Swal.fire({
                                icon: 'error',
                                title: 'Text Not Found',
                                text: `The text file "${item.name}" could not be found. It may have been deleted or moved.`,
                                confirmButtonText: 'OK',
                            });

                            // Refresh to show current state
                            loadFolderContents(currentPath);
                            return false;
                        }

                        // Handle text name already exists
                        if (
                            error.response.data &&
                            (error.response.data.errorCode === 'FILE_EXISTS' ||
                                error.response.data.message?.includes('already exists'))
                        ) {
                            Swal.fire({
                                icon: 'error',
                                title: 'Name Already Exists',
                                text: `A text file named "${newName}" already exists in this location.`,
                                confirmButtonText: 'OK',
                            });
                            return false;
                        }
                    }

                    // Generic error handling
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed to rename text file',
                        text: error.response?.data?.message || error.message || 'An unknown error occurred',
                        confirmButtonText: 'OK',
                    });

                    return Promise.reject(error);
                }
            } else if (item.type === 'audio') {
                const audioId = item.data;
                if (!audioId) {
                    return Promise.reject(new Error('Could not determine audio ID'));
                }

                await audioFileServiceInstance.rename(audioId, newName);
                loadFolderContents(currentPath);
                Swal.fire({
                    icon: 'success',
                    title: 'Renamed',
                    text: `"${item.name}" renamed to "${newName}" successfully`,
                    timer: 1500,
                    showConfirmButton: false,
                });
                return true;
            }
        } catch (error) {
            console.error('Rename error:', error);

            // Fallback error handling
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An unexpected error occurred during the rename operation',
                confirmButtonText: 'OK',
            });

            return Promise.reject(error);
        }
    };

    const handleDownloadItem = async (item) => {
        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (!userData) return;

        if (item.type === 'video') {
            // Video file renaming logic
            const videoId = item.data;
            if (!videoId) {
                return Promise.reject(new Error('Could not determine video ID'));
            }

            try {
                await videoDownloadServiceInstance.downloadVideoFiles(videoId, item);
            } catch (error) {
                console.error('Download video error:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Download Failed',
                    text: error.response?.data?.message || error.message || 'An unknown error occurred during download',
                    confirmButtonText: 'OK',
                });
            } finally {
                setFileUploading(false);
            }

            console.log('Downloading video for item:', item);
        }
    };

    const handleDelete = async (item) => {
        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (!userData) return false;

        const itemPath = `${currentPath}/${item.name}`.replace(/\/\//g, '/');
        setFileUploading(true);

        try {
            if (item.isFolder) {
                await fileSystemAccessServiceInstance.delete(itemPath, userData.email, userData.access);
            } else if (item.type === 'video') {
                const videoId = item.data;
                if (!videoId) {
                    throw new Error('Could not determine video ID');
                }

                await videoCRUDServiceInstance.delete(videoId, currentPath, userData.email, userData.access);
            } else if (item.type === 'text') {
                const textId = item.data;
                if (!textId) {
                    throw new Error('Could not determine text ID');
                }

                await textFileServiceInstance.delete(textId);
            } else if (item.type === 'audio') {
                const audioId = item.data;
                if (!audioId) {
                    throw new Error('Could not determine audio ID');
                }

                await audioFileServiceInstance.delete(audioId);
            } else if (item.type === 'book') {
                await fileSystemAccessServiceInstance.delete(itemPath, userData.email, userData.access);
            }

            loadFolderContents(currentPath);
            Swal.fire({
                icon: 'success',
                title: 'Deleted',
                text: `${item.name} was moved to trash successfully`,
                timer: 1500,
                showConfirmButton: false,
            });

            return true;
        } catch (error) {
            console.error('Delete error:', error);

            // Special handling for non-empty folders
            if (
                item.isFolder &&
                error.response &&
                error.response.data &&
                error.response.data.stringMap &&
                error.response.data.stringMap.errorCode === 'FOLDER_NOT_EMPTY'
            ) {
                const itemCount = error.response.data.stringMap.itemCount || 'multiple';
                setFileUploading(false);

                const forceResult = await Swal.fire({
                    title: 'Folder is not empty',
                    text: `This folder contains ${itemCount} item(s). Do you want to move all contents to trash?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3085d6',
                    confirmButtonText: 'Yes, move everything to trash!',
                    cancelButtonText: 'Cancel',
                });

                if (forceResult.isConfirmed) {
                    setFileUploading(true);

                    try {
                        await fileSystemAccessServiceInstance.delete(
                            itemPath,
                            userData.email,
                            userData.access,
                            true, // Force parameter
                        );

                        loadFolderContents(currentPath);
                        Swal.fire({
                            icon: 'success',
                            title: 'Deleted',
                            text: `${item.name} and its contents were moved to trash successfully`,
                            timer: 1500,
                            showConfirmButton: false,
                        });

                        return true;
                    } catch (forceError) {
                        console.error('Force delete error:', forceError);
                        setFileUploading(false);
                        Swal.fire(
                            'Error',
                            'Failed to trash folder: ' + (forceError.response?.data?.message || 'Unknown error'),
                            'error',
                        );
                        throw forceError;
                    }
                } else {
                    setFileUploading(false);
                    return false;
                }
            } else {
                setFileUploading(false);

                Swal.fire('Error', 'Failed to trash: ' + (error.response?.data?.message || 'Unknown error'), 'error');
                throw error;
            }
        }
    };

    useEffect(() => {
        if (!seesTasks(taskSummary)) {
            setTaskIndex({});
            return undefined;
        }
        const refs = [
            ...videoFiles.map((f) => ({ contentType: 'VIDEO', contentId: f.data })),
            ...textFiles.map((f) => ({ contentType: 'TEXT', contentId: f.data })),
            ...bookFiles.map((f) => ({ contentType: 'BOOK', contentId: f.data })),
        ].filter((r) => r.contentId);
        if (refs.length === 0) {
            setTaskIndex({});
            return undefined;
        }
        let alive = true;
        TaskTrackingService.forContents(refs)
            .then((rows) => alive && setTaskIndex(indexTasksByContent(rows)))
            .catch(() => alive && setTaskIndex({}));
        return () => {
            alive = false;
        };
    }, [taskSummary, videoFiles, textFiles, bookFiles, taskRefresh]);

    const taskIndicators = useMemo(() => ({ index: taskIndex, openTask: setDrawerTaskId }), [taskIndex]);

    const handleCreateVideo = (path) => {
        setOpen(true);
        setFile('');
        setVideoName('');
        setGoogleLinkURL('');
        setYoutubeLinkURL('');
        setLinkError(false);
        setIsCompressed(false);
        setIsLogging(false);
        setSelectOpenAI(true);
        setSkipTranslationIsChecked(false);
        setOnlyTransliterationIsChecked(false);
        setBitRate('115');
        setCRFValue('28');
        setCurrentPath(path);
    };

    const handleSplitVideo = (path) => {
        setSplitVideoOpen(true);
        setSplitVideoPath(path);
    };

    const handleLinkTypeSelect = (l) => {
        if (l === 'gdrive') {
            setLinkType(LinkTypes.GOOGLE_DRIVE);
        } else if (l === 'youtube') {
            setLinkType(LinkTypes.YOUTUBE);
        } else if (l === 'blob') {
            setLinkType(LinkTypes.BLOB);
        } else if (l === 'local') {
            setLinkType(LinkTypes.LOCAL_FILE);
        }
    };

    const uploadVideo = async (linkType, destinationPath = currentPath, subtitleFile = null, taskFields = null) => {
        return videoUploadServiceInstance.uploadVideo(linkType, {
            setFileUploading,
            googleLinkURL,
            youtubeLinkURL,
            videoName,
            folderPath: destinationPath, // Use the passed in path or default to currentPath
            isCompressed,
            selectOpenAI,
            isLogging,
            skipTranslationIsChecked,
            onlyTransliterationIsChecked,
            bitRate,
            crfValue,
            setOpen,
            selectedFile,
            subtitleFile,
            onSuccess: (created) => {
                // The plain upload passes the new id; the upload with subtitles passes the result object
                const videoId = created && typeof created === 'object' ? created.id || created.videoId : created;
                startTrackingForNewContent('VIDEO', [videoId], taskFields);
                // If destination path is different from current path, navigate to it
                if (destinationPath !== currentPath) {
                    setCurrentPath(destinationPath);
                }
                loadFolderContents(destinationPath);
            },
        });
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleMenuOpen = (event, item) => {
        setMenuAnchorEl(event.currentTarget);
        console.log('Menu opened for item:', item);
        setSelectedItem(item);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedItem(null);
    };

    const isSelectedItemFolder = () => {
        return selectedItem?.isFolder || false;
    };

    const isSelectedItemVideo = () => {
        return selectedItem?.type === 'video' || false;
    };

    const isSelectedItemText = () => {
        return selectedItem?.type === 'text' || false;
    };

    const isSelectedItemAudio = () => {
        return selectedItem?.type === 'audio' || false;
    };

    const isSelectedItemBook = () => {
        return selectedItem?.type === 'book' || false;
    };

    const canTransformDubbing = () => {
        return isAdmin() || isDubber();
    };

    const canDeleteSelectedItem = () => {
        if (!selectedItem) return false;
        if (isAdmin()) return true;
        return isDubber() && selectedItem?.type === 'audio' && !selectedItem?.isFolder;
    };

    const getSelectedItemName = () => {
        if (!selectedItem) {
            return '';
        }

        if (selectedItem.isFolder) {
            return selectedItem.name;
        }

        if (isSelectedItemVideo()) {
            return selectedItem.videoName || '';
        }

        return selectedItem.name;
    };

    const handleShowProgressStages = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        setSelectedVideoForProgressStages(videoId);
        setProgressStagesDialogOpen(true);
    };

    const handleShowGlossaryEditor = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        navigate(`/glossary/${videoId}`, { state: { fromPath: currentPath } });
    };

    const handleShowGlossaryEditorForTextFile = (file) => {
        const textFileId = file.data;

        if (!textFileId) {
            Swal.fire('Error', 'Could not determine text file ID', 'error');
            return;
        }

        navigate(`/glossary/textfile/${textFileId}`, {
            state: {
                backTo: `/textfiles/edit/${textFileId}`,
                fromPath: currentPath,
            },
        });
    };

    const handleShowHistory = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        setSelectedVideoForHistory(videoId);
        setHistoryDialogOpen(true);
    };

    const handleDubbingTransform = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        setSelectedVideoForDubbingTransformation(videoId);
        setDubbingTransformationDialogOpen(true);
    };

    const handleCreateAudio = (path) => {
        setSelectedVideoForDubbingTransformation(null);
        setCurrentPath(path);
        setDubbingTransformationDialogOpen(true);
    };

    const handleDubSync = (file) => {
        const videoId = file?.data;
        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }
        handleMenuClose();
        // Navigate to the Video Editor with openDubSync flag — the editor will
        // auto-open the Add DubSync Track dialog on load.
        navigate(`/videos/video-editor/${videoId}`, {
            state: { videoData: file?.videoData || null, from: currentPath, openDubSync: true },
        });
    };

    const handleDubbingTransformClose = (status, message) => {
        setDubbingTransformationDialogOpen(false);
        setSelectedVideoForDubbingTransformation(null);

        if (status === 'success') {
            loadFolderContents(currentPath);
            Swal.fire('Success', message || 'Dubbing transformation started successfully.', 'success');
        } else if (status === 'error') {
            Swal.fire('Error', message || 'Failed to start dubbing transformation.', 'error');
        }
    };

    const handleRecompressVideo = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        Swal.fire({
            title: 'Redo Compression?',
            html: `Re-compress <strong>${file.videoName || 'this video'}</strong> to improve loading speed and seeking.<br/><br/>The original subtitles will not be affected. This runs in the background and may take a few minutes.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, re-compress it',
            cancelButtonText: 'Cancel',
            showLoaderOnConfirm: true,
            preConfirm: () => {
                return videoUploadServiceInstance
                    .recompress(videoId, file.videoName || file.name)
                    .then((success) => {
                        if (!success) {
                            Swal.showValidationMessage('Server accepted the request but returned an unexpected response. Check the progress panel.');
                        }
                        return success;
                    })
                    .catch((error) => {
                        Swal.showValidationMessage(`Request failed: ${error.response?.data?.message || error.message}`);
                    });
            },
        }).then((result) => {
            if (result.isConfirmed && result.value !== false) {
                Swal.fire({
                    icon: 'info',
                    title: 'Re-compression queued',
                    html: `<strong>${file.videoName || 'The video'}</strong> is being re-compressed in the background.<br/><br/>You can track progress in the processing panel. The video will be ready when the status returns to <em>Completed</em>.`,
                    confirmButtonText: 'OK',
                });
                loadFolderContents(currentPath);
            }
        });
    };

    const handleRegenerateFile = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        Swal.fire({
            title: 'Regenerate Video Processing',
            text: 'This will restart the processing of this video. Do you want to continue?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, regenerate it',
            cancelButtonText: 'Cancel',
            showLoaderOnConfirm: true,
            preConfirm: () => {
                return videoUploadServiceInstance
                    .regenerate(videoId, file.videoName)
                    .then((response) => {
                        return response;
                    })
                    .catch((error) => {
                        Swal.showValidationMessage(`Request failed: ${error.response?.data?.message || error.message}`);
                    });
            },
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    icon: 'success',
                    title: 'Regeneration Started',
                    text: 'The video processing has been restarted.',
                    timer: 1500,
                    showConfirmButton: false,
                });
                loadFolderContents(currentPath);
            }
        });
    };

    // Add this function where other handlers are defined
    const handleViewMultiColumn = async (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Could not determine video ID',
            });
            return;
        }

        try {
            const userData = JSON.parse(localStorage.getItem('authUser'));
            const response = await videoCRUDServiceInstance.getVideoById(videoId, userData.email, userData.access);
            const fullVideoData = response.data;

            // Store full video props in localStorage for the MultiColumnSubtitleViewer to use
            localStorage.setItem('videoProps', JSON.stringify(fullVideoData));

            // Navigate to the multi-column viewer
            navigate(`/multi-column-viewer/${videoId}`);
        } catch (error) {
            console.error('Navigation error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Navigation Error',
                text: 'Failed to navigate to multi-column view',
            });
        }

        // Close the context menu
        handleMenuClose();
    };

    const handleAddLanguages = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        setSelectedVideoForText2Text(videoId);
        setText2TextDialogOpen(true);
    };

    const handleText2TextDialogClose = () => {
        setText2TextDialogOpen(false);
        fetchFileLanguages(videoFiles);
    };

    const handleText2TextSuccess = (result) => {
        console.log('Translation initiated:', result);
    };

    const handleDuplicateItem = async (item) => {
        if (!item || item.isFolder) {
            Swal.fire('Error', 'Only video files can be duplicated', 'error');
            return;
        }

        const videoId = item.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        try {
            setFileUploading(true);
            const duplicatedVideo = await videoCRUDServiceInstance.duplicate(videoId);
            setFileUploading(false);

            Swal.fire({
                icon: 'success',
                title: 'Video Duplicated',
                text: `The video has been duplicated successfully with ID: ${duplicatedVideo.videoName}`,
                timer: 5000,
                showConfirmButton: false,
            });

            loadFolderContents(currentPath);
        } catch (error) {
            setFileUploading(false);
            console.error('Error duplicating video:', error);
            Swal.fire('Error', 'Failed to duplicate video', 'error');
        }
    };

    const handleMoveItem = (item) => {
        if (!item) return;

        if (item.isFolder) {
            setItemToMove({
                isFolder: true,
                path: item.path || `${currentPath}/${item.name}`.replace(/\/\//g, '/'),
                name: item.name,
                item: item,
            });
        } else {
            const videoOrTextId = item.data;

            if (!videoOrTextId) {
                Swal.fire('Error', 'Could not determine item ID', 'error');
                return;
            }

            setItemToMove({
                isFolder: false,
                id: videoOrTextId,
                name: item.videoName || item.name,
                item: item,
            });
        }

        setMoveDialogOpen(true);
    };

    const handleMoveToFolder = (destinationPath) => {
        if (!itemToMove) {
            Swal.fire('Error', 'No item selected for moving', 'error');
            return;
        }

        setFileUploading(true);

        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (!userData) {
            Swal.fire('Error', 'User authentication data not found', 'error');
            return;
        }

        fileSystemAccessServiceInstance
            .moveItem(itemToMove, currentPath, destinationPath, userData.email, userData.access)
            .then(() => {
                setFileUploading(false);
                Swal.fire({
                    icon: 'success',
                    title: `${itemToMove.isFolder ? 'Folder' : 'File'} Moved`,
                    text: `The ${itemToMove.isFolder ? 'folder' : 'file'} has been moved successfully.`,
                    timer: 1500,
                    showConfirmButton: false,
                });
                loadFolderContents(currentPath);
            })
            .catch((error) => {
                setFileUploading(false);
                console.error(`Error moving ${itemToMove.isFolder ? 'folder' : 'file'}:`, error);

                if (error.response?.data?.errorCode === 'TARGET_EXISTS') {
                    Swal.fire({
                        title: 'Target Already Exists',
                        text: 'Would you like to replace the existing item?',
                        icon: 'question',
                        showCancelButton: true,
                        confirmButtonText: 'Yes, replace',
                        cancelButtonText: 'No, keep both',
                    }).then((result) => {
                        if (result.isConfirmed) {
                            fileSystemAccessServiceInstance
                                .moveItem(
                                    itemToMove,
                                    currentPath,
                                    destinationPath,
                                    userData.email,
                                    userData.access,
                                    true, // force replacement
                                )
                                .then(() => {
                                    setFileUploading(false);
                                    Swal.fire({
                                        icon: 'success',
                                        title: 'Item Replaced',
                                        text: 'The item has been moved and replaced the existing one.',
                                        timer: 1500,
                                        showConfirmButton: false,
                                    });
                                    loadFolderContents(currentPath);
                                })
                                .catch((replaceError) => {
                                    setFileUploading(false);
                                    console.error('Error replacing item:', replaceError);
                                    Swal.fire(
                                        'Error',
                                        replaceError.response?.data?.message || 'Failed to replace item',
                                        'error',
                                    );
                                });
                        }
                    });
                } else {
                    Swal.fire(
                        'Error',
                        error.response?.data?.message || `Failed to move ${itemToMove.isFolder ? 'folder' : 'file'}`,
                        'error',
                    );
                }
            });
    };

    const handleShowAssignDialog = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        setSelectedVideoForAssign({
            ...file,
            id: videoId,
        });
        setAssignVideoDialogOpen(true);
    };

    // Add this new handler function
    const handleShowSectionManager = (file) => {
        const videoId = file.data;

        if (!videoId) {
            // Show error if no video ID
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Could not determine video ID',
            });
            return;
        }

        setSelectedFileForSections(file);
        setSectionManagerDialogOpen(true);
        handleMenuClose(); // Close the context menu
    };

    const handleNavigateToTrashBin = () => {
        navigate('/trash');
    };

    // Add handler for VO script dialog
    const handleShowVoiceOverDialog = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        setSelectedVideoForVoiceOver(videoId);
        setVoiceOverDialogOpen(true);
    };

    const handleVoiceOverDialogClose = (status, message) => {
        setVoiceOverDialogOpen(false);

        if (status === 'success') {
            Swal.fire({
                icon: 'success',
                title: 'VO script Generation Started',
                text: message,
                timer: 3000,
            });
        } else if (status === 'error') {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: message,
            });
        }

        // Refresh the file list to show updated status
        fetchFileLanguages(videoFiles);
    };

    const handleVoiceOverSuccess = (result) => {
        console.log('VO script generation initiated:', result);
    };

    const handleOpenReelEditor = (file) => {
        const videoId = file?.data || file?.id;
        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        const availableLanguages = Array.from(
            new Set((file?.videoLanguages || []).map((lang) => lang?.languageName).filter(Boolean)),
        );

        navigate(`/reels/create/${videoId}`, {
            state: {
                folderPath: currentPath,
                videoData: {
                    id: videoId,
                    videoName: file?.videoName || file?.name || '',
                    availableLanguages,
                },
            },
        });

        handleMenuClose();
    };

    const handleBulkDelete = async (items) => {
        if (!items || items.length === 0) return;

        const result = await Swal.fire({
            title: `Move ${items.length} file${items.length > 1 ? 's' : ''} to trash?`,
            text: `Are you sure you want to trash ${items.length} file${items.length > 1 ? 's' : ''}? This can be undone from the Trash bin.${trashTasksNote(items, taskIndex)}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, move to trash',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        setFileUploading(true);
        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (!userData) {
            setFileUploading(false);
            return;
        }

        let failCount = 0;
        for (const item of items) {
            try {
                if (item.type === 'video') {
                    await videoCRUDServiceInstance.delete(item.data, currentPath, userData.email, userData.access);
                } else if (item.type === 'text') {
                    await textFileServiceInstance.delete(item.data);
                } else if (item.type === 'audio') {
                    await audioFileServiceInstance.delete(item.data);
                }
            } catch (error) {
                console.error(`Failed to delete ${item.name}:`, error);
                failCount++;
            }
        }

        setFileUploading(false);
        loadFolderContents(currentPath);

        if (failCount === 0) {
            Swal.fire({
                icon: 'success',
                title: 'Deleted',
                text: `${items.length} file${items.length > 1 ? 's' : ''} moved to trash successfully`,
                timer: 2000,
                showConfirmButton: false,
            });
        } else {
            Swal.fire(
                'Partial Success',
                `${items.length - failCount} of ${items.length} files moved to trash. ${failCount} failed.`,
                'warning',
            );
        }
    };

    return (
        <>
            <Backdrop sx={{ color: '#FFF', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={fileUploading}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <TaskIndicatorsContext.Provider value={taskIndicators}>
            <ModernFolderView
                folders={folders}
                videoFiles={videoFiles}
                textFiles={textFiles}
                audioFiles={audioFiles}
                bookFiles={bookFiles}
                currentPath={currentPath}
                onFolderClick={handleFolderClick}
                onVideoFileClick={handleVideoFileClick}
                onTextFileClick={handleTextFileClick}
                onAudioFileClick={handleAudioFileClick}
                onBookFileClick={handleBookFileClick}
                onCreateFolder={handleCreateFolder}
                onRename={handleRename}
                onDelete={handleDelete}
                onBulkDelete={handleBulkDelete}
                onCreateVideo={handleCreateVideo}
                onSplitVideo={handleSplitVideo}
                onCreateAudio={handleCreateAudio}
                onMenuOpen={handleMenuOpen}
                onRefresh={() => loadFolderContents(currentPath)}
                onTranslateTextFiles={handleTextFileTranslate}
                onDownloadTextFilesByLanguage={() => { setDownloadByLangLanguages(new Set()); setDownloadByLangFormats(new Set(['docx'])); setDownloadByLangFileIds(new Set(textFiles.map(tf => tf.data))); setDownloadByLangDialogOpen(true); }}
                videoExperience={
                    fileSystemAccessServiceInstance.getVideoExperience &&
                    fileSystemAccessServiceInstance.getVideoExperience()
                }
                themeVariant={themeVariant}
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                onSrtSearchResultClick={handleSrtSearchResultClick}
            />
            </TaskIndicatorsContext.Provider>
            <TaskDrawer
                open={!!drawerTaskId}
                taskId={drawerTaskId}
                onClose={() => setDrawerTaskId(null)}
                onOpenTask={setDrawerTaskId}
                onChanged={() => setTaskRefresh((n) => n + 1)}
                setCanNavigateToEdit={setCanNavigateToEdit}
            />

            <Box
                sx={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    zIndex: 1000,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    padding: '4px',
                    backdropFilter: 'blur(5px)',
                }}
            >
                <Tooltip title="Trash">
                    <Button
                        color="secondary"
                        startIcon={<DeleteIcon />}
                        onClick={handleNavigateToTrashBin}
                        variant="outlined"
                        size="small"
                        sx={{ ml: 1 }}
                    >
                        Trash
                    </Button>
                </Tooltip>
            </Box>

            {/* Dialogs and menus */}
            <UploadVideoDialog
                open={open}
                handleClose={handleClose}
                videoName={videoName}
                setVideoName={setVideoName}
                googleLinkURL={googleLinkURL}
                setGoogleLinkURL={setGoogleLinkURL}
                youtubeLinkURL={youtubeLinkURL}
                setYoutubeLinkURL={setYoutubeLinkURL}
                isLinkError={linkError}
                getLanguagesForCreateVideo={getLanguagesForCreateVideo}
                isCompressed={isCompressed}
                setIsCompressed={setIsCompressed}
                selectOpenAI={selectOpenAI}
                setSelectOpenAI={setSelectOpenAI}
                isLogging={isLogging}
                setIsLogging={setIsLogging}
                skipTranslationIsChecked={skipTranslationIsChecked}
                setSkipTranslationIsChecked={setSkipTranslationIsChecked}
                onlyTransliterationIsChecked={onlyTransliterationIsChecked}
                setOnlyTransliterationIsChecked={setOnlyTransliterationIsChecked}
                bitRate={bitRate}
                setBitRate={setBitRate}
                crfValue={crfValue}
                setCRFValue={setCRFValue}
                uploadVideo={uploadVideo}
                setOpen={setOpen}
                linkType={linkType}
                handleLinkTypeSelect={handleLinkTypeSelect}
                setLinkError={setLinkError}
                onFileSelect={setSelectedFile}
                fileName={selectedFile}
                folderPath={currentPath} // Pass current path
                darkMode={darkMode}
                themeVariant={themeVariant}
            />

            <SplitVideoDialog
                open={splitVideoOpen}
                onClose={() => setSplitVideoOpen(false)}
                folderPath={splitVideoPath}
                getLanguagesForCreateVideo={getLanguagesForCreateVideo}
                darkMode={darkMode}
                themeVariant={themeVariant}
                onSuccess={() => loadFolderContents(splitVideoPath)}
            />

            <StartTrackingDialog open={trackDialogOpen} onClose={() => setTrackDialogOpen(false)} files={trackFiles} />
            <ManageTaskDialog
                open={manageTaskOpen}
                onClose={() => setManageTaskOpen(false)}
                file={trackFiles[0]}
                canStartTracking={!!taskSummary?.canManage}
                onStartTracking={() => {
                    setManageTaskOpen(false);
                    setTrackDialogOpen(true);
                }}
            />

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                data-testid="folder-context-menu"
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        width: 400,
                        '& .MuiMenuItem-root': {
                            borderRadius: '4px',
                            mx: 0.5,
                            my: 0.3,
                        },
                    },
                }}
            >
                {/* ORGANIZATION GROUP - Change to purple */}
                <Box
                    sx={{
                        px: 2,
                        py: 0.5,
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        fontWeight: 'medium',
                        textTransform: 'uppercase',
                        borderLeft: '3px solid',
                        borderColor: 'purple.main', // Change to purple
                        backgroundColor: 'rgba(128, 0, 128, 0.05)', // Light purple background
                        borderRadius: '0 4px 4px 0',
                    }}
                >
                    Organization
                </Box>

                <MenuItem
                    data-testid="menu-rename"
                    onClick={() => {
                        const itemToRename = selectedItem;
                        const currentName = getSelectedItemName();
                        // Close the MUI menu before opening the SweetAlert dialog.
                        // Otherwise the menu's focus trap stays mounted underneath
                        // the dialog and keeps stealing focus, so typing in the
                        // SweetAlert input silently does nothing.
                        handleMenuClose();

                        if (itemToRename) {
                            Swal.fire({
                                title: 'Rename',
                                input: 'text',
                                inputValue: currentName,
                                showCancelButton: true,
                                confirmButtonText: 'Rename',
                                inputValidator: (value) => {
                                    // First check if the name is valid using our utility
                                    const validationError = validateItemName(
                                        value,
                                        itemToRename.isFolder ? 'folder' : 'file',
                                    );

                                    if (validationError) {
                                        return validationError;
                                    }

                                    // Then check if name is unchanged
                                    if (value === currentName) {
                                        return 'Please enter a different name';
                                    }
                                },
                            }).then((result) => {
                                if (result.isConfirmed && result.value) {
                                    handleRename(itemToRename, result.value);
                                }
                            });
                        }
                    }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" color="secondary" />
                    </ListItemIcon>
                    <ListItemText>Rename</ListItemText>
                </MenuItem>

                <MenuItem
                    data-testid="menu-move"
                    onClick={() => {
                        handleMoveItem(selectedItem);
                        handleMenuClose();
                    }}
                >
                    <ListItemIcon>
                        <DriveFileMoveIcon fontSize="small" color="secondary" />
                    </ListItemIcon>
                    <ListItemText>Move to Folder</ListItemText>
                </MenuItem>

                {selectedItem && isSelectedItemVideo() && (
                    <>
                        <MenuItem
                            data-testid="menu-duplicate"
                            onClick={() => {
                                handleDuplicateItem(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <FileCopyIcon fontSize="small" color="secondary" />
                            </ListItemIcon>
                            <ListItemText>Duplicate</ListItemText>
                        </MenuItem>

                        <MenuItem
                            data-testid="menu-download"
                            onClick={() => {
                                handleDownloadItem(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <DownloadIcon fontSize="small" color="secondary" />
                            </ListItemIcon>
                            <ListItemText>Download</ListItemText>
                        </MenuItem>

                    </>
                )}

                <Divider sx={{ my: 1 }} />

                {/* STATUS GROUP - Success green with left border */}
                <Box
                    sx={{
                        px: 2,
                        py: 0.5,
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        fontWeight: 'medium',
                        textTransform: 'uppercase',
                        borderLeft: '3px solid',
                        borderColor: 'success.main', // Green border
                        backgroundColor: 'rgba(46, 125, 50, 0.08)',
                        borderRadius: '0 4px 4px 0',
                    }}
                >
                    Status & Progress
                </Box>
                {(taskSummary?.canManage
                    ? isSelectedItemVideo() || isSelectedItemText() || isSelectedItemBook()
                    : isSelectedItemVideo() && savesSrt(taskIndex, selectedItem?.data)) && (
                    <MenuItem
                        data-testid="menu-manage-task"
                        onClick={() => {
                            setTrackFiles([selectedItem]);
                            setManageTaskOpen(true);
                            handleMenuClose();
                        }}
                    >
                        <ListItemIcon>
                            <AssignmentIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText>Manage task</ListItemText>
                    </MenuItem>
                )}
                {selectedItem && isSelectedItemFolder() && (
                    <>
                        <MenuItem
                            data-testid="menu-folder-history"
                            onClick={() => {
                                handleShowFolderHistory(selectedItem);
                            }}
                        >
                            <ListItemIcon>
                                <HistoryIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>View Folder History</ListItemText>
                        </MenuItem>
                    </>
                )}

                {selectedItem && isSelectedItemVideo() && (
                    <>
                        <MenuItem
                            data-testid="menu-progress-stages"
                            onClick={() => {
                                handleShowProgressStages(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <TimelineIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>Manage Progress Stages</ListItemText>
                        </MenuItem>

                        <MenuItem
                            data-testid="menu-history"
                            onClick={() => {
                                handleShowHistory(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <HistoryIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>See File Details</ListItemText>
                        </MenuItem>

                        {(isAdmin() || isGlossaryManager()) && (
                        <MenuItem
                            data-testid="menu-glossary-editor"
                            onClick={() => {
                                handleShowGlossaryEditor(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <MenuBookIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>Glossary Editor</ListItemText>
                        </MenuItem>
                        )}

                        <MenuItem onClick={() => handleViewMultiColumn(selectedItem)}>
                            <ListItemIcon>
                                <CompareIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>Subtitle Version Viewer</ListItemText>
                        </MenuItem>

                        <Divider sx={{ my: 1 }} />

                        {/* SUBTITLE GROUP - Info blue with left border */}
                        <Box
                            sx={{
                                px: 2,
                                py: 0.5,
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                fontWeight: 'medium',
                                textTransform: 'uppercase',
                                borderLeft: '3px solid',
                                borderColor: 'info.main', // Blue border
                                backgroundColor: 'rgba(2, 136, 209, 0.08)',
                                borderRadius: '0 4px 4px 0',
                            }}
                        >
                            Subtitle Management
                        </Box>

                        {/* Hidden: Manage Subtitle Sections */}
                        {false && (
                        <MenuItem
                            data-testid="menu-manage-sections"
                            onClick={() => {
                                handleShowSectionManager(selectedItem);
                            }}
                        >
                            <ListItemIcon>
                                <SplitScreenIcon fontSize="small" color="info" />
                            </ListItemIcon>
                            <ListItemText>Manage Subtitle Sections</ListItemText>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.65rem',
                                    fontStyle: 'italic',
                                    ml: 1,
                                    color: isAdmin() ? 'primary.main' : 'text.secondary',
                                    opacity: isAdmin() ? 0.7 : 0.9,
                                    bgcolor: isAdmin() ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                    px: isAdmin() ? 0.8 : 0,
                                    py: isAdmin() ? 0.2 : 0,
                                    borderRadius: isAdmin() ? 1 : 0,
                                }}
                            >
                                Coming Soon ...
                            </Typography>
                        </MenuItem>
                        )}

                        <MenuItem
                            data-testid="menu-text2text"
                            onClick={() => {
                                handleAddLanguages(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <TranslateIcon fontSize="small" color="info" />
                            </ListItemIcon>
                            <ListItemText>Text2Text Translations</ListItemText>
                        </MenuItem>

                        <MenuItem
                            data-testid="menu-open-editor-v1"
                            onClick={() => {
                                handleOpenEditorV1(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <AutoAwesomeIcon fontSize="small" color="info" />
                            </ListItemIcon>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <ListItemText>Open in Editor V1</ListItemText>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontStyle: 'italic', ml: 1, color: 'info.main', opacity: 0.8 }}>Legacy</Typography>
                            </Box>
                        </MenuItem>

                        <MenuItem
                            data-testid="menu-voice-over"
                            onClick={() => {
                                handleShowVoiceOverDialog(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <RecordVoiceOverIcon fontSize="small" color="info" />
                            </ListItemIcon>
                            <ListItemText>Generate VO script</ListItemText>
                        </MenuItem>

                        {/* ── Video / Audio Processing ──────────────────── */}
                        <Divider sx={{ my: 1 }} />

                        {/* VIDEO/AUDIO GROUP - Amber/warning with left border */}
                        <Box
                            sx={{
                                px: 2,
                                py: 0.5,
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                fontWeight: 'medium',
                                textTransform: 'uppercase',
                                borderLeft: '3px solid',
                                borderColor: 'warning.main',
                                backgroundColor: 'rgba(237, 108, 2, 0.08)',
                                borderRadius: '0 4px 4px 0',
                            }}
                        >
                            Video / Audio Processing
                        </Box>

                        <MenuItem
                            data-testid="menu-open-reel-editor"
                            onClick={() => {
                                handleOpenReelEditor(selectedItem);
                            }}
                        >
                            <ListItemIcon>
                                <MovieCreationIcon fontSize="small" color="warning" />
                            </ListItemIcon>
                            <ListItemText>Open Reel Editor</ListItemText>
                        </MenuItem>

                        <MenuItem
                            data-testid="menu-open-video-editor"
                            onClick={() => {
                                handleOpenVideoEditor(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <VideoSettingsIcon fontSize="small" color="warning" />
                            </ListItemIcon>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <ListItemText>Open in Video Editor</ListItemText>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontStyle: 'italic', ml: 1, color: 'warning.main', opacity: 0.8 }}>Studio</Typography>
                            </Box>
                        </MenuItem>

                        {/* DubSync removed — now accessible inline from the Video Editor */}

                        {/* Hidden: Assign Video */}
                        {false && (
                        <MenuItem
                            data-testid="menu-assign"
                            onClick={() => {
                                if (isAdmin()) {
                                    handleShowAssignDialog(selectedItem);
                                    handleMenuClose();
                                }
                            }}
                            disabled={!isAdmin()}
                            sx={{
                                opacity: isAdmin() ? 1 : 0.6,
                            }}
                        >
                            <ListItemIcon>
                                <AssignmentIcon fontSize="small" color="info" />
                            </ListItemIcon>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <ListItemText>Assign Video</ListItemText>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.65rem',
                                        fontStyle: 'italic',
                                        ml: 1,
                                        color: isAdmin() ? 'primary.main' : 'text.secondary',
                                        opacity: isAdmin() ? 0.7 : 0.9,
                                        bgcolor: isAdmin() ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                        px: isAdmin() ? 0.8 : 0,
                                        py: isAdmin() ? 0.2 : 0,
                                        borderRadius: isAdmin() ? 1 : 0,
                                    }}
                                >
                                    admin
                                </Typography>
                            </Box>
                        </MenuItem>
                        )}
                    </>
                )}

                {selectedItem && isSelectedItemAudio() && (
                    <>
                        <MenuItem
                            data-testid="menu-audio-file-details"
                            onClick={() => {
                                handleShowAudioFileDetails(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <HistoryIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>See File Details</ListItemText>
                        </MenuItem>
                    </>
                )}

                {selectedItem && isSelectedItemText() && (
                    <>
                        <MenuItem
                            data-testid="menu-text-file-details"
                            onClick={() => {
                                handleShowTextFileDetails(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <HistoryIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>See File Details</ListItemText>
                        </MenuItem>

                        {(isAdmin() || isGlossaryManager()) && (
                        <MenuItem
                            data-testid="menu-text-glossary-editor"
                            onClick={() => {
                                handleShowGlossaryEditorForTextFile(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <MenuBookIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>Glossary Editor</ListItemText>
                        </MenuItem>
                        )}
                    </>
                )}

                {selectedItem && isSelectedItemBook() && (
                    <>
                        <MenuItem
                            data-testid="menu-book-file-details"
                            onClick={() => {
                                handleShowBookFileDetails(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <HistoryIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>See File Details</ListItemText>
                        </MenuItem>
                    </>
                )}

                {/* DANGER ZONE - Error red with more visual emphasis */}
                <Divider sx={{ my: 1 }} />

                <Box
                    sx={{
                        px: 2,
                        py: 0.5,
                        fontSize: '0.75rem',
                        color: 'error.main',
                        fontWeight: 'bold', // Make it bolder
                        textTransform: 'uppercase',
                        borderLeft: '3px solid',
                        borderColor: 'error.main',
                        backgroundColor: 'rgba(211, 47, 47, 0.08)', // Light red background
                        borderRadius: '0 4px 4px 0', // Round the right corners
                    }}
                >
                    Are you sure?
                </Box>

                {/* Only show Regenerate for files, not folders, and only for admins */}
                {selectedItem && isSelectedItemVideo() && (
                    <div>
                        <MenuItem
                            data-testid="menu-recompress"
                            onClick={() => {
                                if (isAdmin()) {
                                    handleRecompressVideo(selectedItem);
                                    handleMenuClose();
                                }
                            }}
                            disabled={!isAdmin()}
                            sx={{
                                color: 'error.main',
                                opacity: isAdmin() ? 1 : 0.6,
                            }}
                        >
                            <ListItemIcon>
                                <VideoSettingsIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <ListItemText>Redo Compression</ListItemText>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.65rem',
                                        fontStyle: 'italic',
                                        ml: 1,
                                        color: isAdmin() ? 'primary.main' : 'text.secondary',
                                        opacity: isAdmin() ? 0.7 : 0.9,
                                        bgcolor: isAdmin() ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                        px: isAdmin() ? 0.8 : 0,
                                        py: isAdmin() ? 0.2 : 0,
                                        borderRadius: isAdmin() ? 1 : 0,
                                    }}
                                >
                                    admin
                                </Typography>
                            </Box>
                        </MenuItem>

                        <MenuItem
                            data-testid="menu-regenerate"
                            onClick={() => {
                                if (isAdmin()) {
                                    handleRegenerateFile(selectedItem);
                                    handleMenuClose();
                                }
                            }}
                            disabled={!isAdmin()}
                            sx={{
                                color: 'error.main',
                                opacity: isAdmin() ? 1 : 0.6,
                            }}
                        >
                            <ListItemIcon>
                                <RefreshIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <ListItemText>Regenerate</ListItemText>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.65rem',
                                        fontStyle: 'italic',
                                        ml: 1,
                                        color: isAdmin() ? 'primary.main' : 'text.secondary',
                                        opacity: isAdmin() ? 0.7 : 0.9,
                                        bgcolor: isAdmin() ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                        px: isAdmin() ? 0.8 : 0,
                                        py: isAdmin() ? 0.2 : 0,
                                        borderRadius: isAdmin() ? 1 : 0,
                                    }}
                                >
                                    admin
                                </Typography>
                            </Box>
                        </MenuItem>
                    </div>
                )}

                <MenuItem
                    data-testid="menu-delete"
                    onClick={() => {
                        if (selectedItem && canDeleteSelectedItem()) {
                            Swal.fire({
                                title: 'Are you sure?',
                                text: `Do you want to move "${getSelectedItemName()}" to Trash?${trashTasksNote([selectedItem], taskIndex)}`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonText: 'Move to Trash',
                                confirmButtonColor: '#d33',
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    handleDelete(selectedItem)
                                        .catch(() => {
                                            // Error handled inside handleDelete
                                        })
                                        .finally(() => {
                                            handleMenuClose();
                                        });
                                } else {
                                    handleMenuClose();
                                }
                            });
                        } else {
                            handleMenuClose();
                        }
                    }}
                    disabled={!canDeleteSelectedItem()}
                    sx={{
                        color: 'error.main',
                        opacity: canDeleteSelectedItem() ? 1 : 0.6,
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <ListItemText>Move to Trash</ListItemText>
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '0.65rem',
                                fontStyle: 'italic',
                                ml: 1,
                                color: canDeleteSelectedItem() ? 'primary.main' : 'text.secondary',
                                opacity: canDeleteSelectedItem() ? 0.7 : 0.9,
                                bgcolor: canDeleteSelectedItem() ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                px: canDeleteSelectedItem() ? 0.8 : 0,
                                py: canDeleteSelectedItem() ? 0.2 : 0,
                                borderRadius: canDeleteSelectedItem() ? 1 : 0,
                            }}
                        >
                            {isAdmin() ? 'admin' : isDubber() && selectedItem?.type === 'audio' ? 'dubber' : ''}
                        </Typography>
                    </Box>
                </MenuItem>

                {/* Add the Manage Permissions menu item here */}
                {isAdmin() && (
                    <MenuItem
                        data-testid="menu-permissions"
                        onClick={() => {
                            handleShowPermissionsDialog(selectedItem);
                            handleMenuClose();
                        }}
                        sx={{
                            color: 'error.main',
                            opacity: isAdmin() ? 1 : 0.6,
                        }}
                    >
                        <ListItemIcon>
                            <LockIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                            }}
                        >
                            <ListItemText>Manage Permissions</ListItemText>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.65rem',
                                    fontStyle: 'italic',
                                    ml: 1,
                                    color: isAdmin() ? 'primary.main' : 'text.secondary',
                                    opacity: isAdmin() ? 0.7 : 0.9,
                                    bgcolor: isAdmin() ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                    px: isAdmin() ? 0.8 : 0,
                                    py: isAdmin() ? 0.2 : 0,
                                    borderRadius: isAdmin() ? 1 : 0,
                                }}
                            >
                                admin
                            </Typography>
                        </Box>
                    </MenuItem>
                )}
            </Menu>

            <Text2TextDialog
                open={text2TextDialogOpen}
                onClose={handleText2TextDialogClose}
                videoId={selectedVideoForText2Text}
                onSuccess={handleText2TextSuccess}
                folderPath={currentPath}
            />
            <ProgressStagesDialog
                open={progressStagesDialogOpen}
                onClose={() => {
                    setProgressStagesDialogOpen(false);
                    // If changes were made, schedule a refresh
                    if (stagesDialogChangesMade) {
                        setRefreshFlag((prev) => prev + 1);
                    }
                }}
                videoId={selectedVideoForProgressStages}
                folderPath={currentPath}
                onChangesMade={() => setStagesDialogChangesMade(true)}
            />
            <VideoHistoryDialog
                open={historyDialogOpen}
                onClose={() => {
                    setHistoryDialogOpen(false);
                    // If changes were made, schedule a refresh
                    if (historyDialogChangesMade) {
                        setRefreshFlag((prev) => prev + 1);
                    }
                }}
                videoId={selectedVideoForHistory}
                folderPath={currentPath}
                onChangesMade={() => setHistoryDialogChangesMade(true)}
            />
            <MoveDialog
                open={moveDialogOpen}
                onClose={() => setMoveDialogOpen(false)}
                onMove={handleMoveToFolder}
                fileName={itemToMove?.name}
                currentPath={currentPath}
                isFolder={itemToMove?.isFolder || false}
            />
            <AssignVideoDialog
                open={assignVideoDialogOpen}
                onClose={() => setAssignVideoDialogOpen(false)}
                videoInfo={selectedVideoForAssign}
            />
            <DubbingTransformDialog
                open={dubbingTransformationDialogOpen}
                onClose={handleDubbingTransformClose}
                onTransform={handleDubbingTransform}
                fileName={selectedVideoForDubbingTransformation?.name}
                folderPath={currentPath}
                videoId={selectedVideoForDubbingTransformation}
                isFolder={selectedVideoForDubbingTransformation?.isFolder || false}
            />
            <Dialog
                open={createFolderDialogOpen}
                onClose={() => setCreateFolderDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
                data-testid="create-folder-dialog"
            >
                <DialogTitle>Create New Folder</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="folderName"
                        label="Folder Name"
                        type="text"
                        fullWidth
                        value={newFolderName}
                        onChange={(e) => {
                            setNewFolderName(e.target.value);
                            // Only mark as touched if there's actually a value
                            if (!folderNameTouched) {
                                setFolderNameTouched(true);
                            }
                        }}
                        error={folderNameTouched && !!validateItemName(newFolderName, 'folder')}
                        helperText={folderNameTouched ? validateItemName(newFolderName, 'folder') || '' : ''}
                        data-testid="folder-name-input"
                    />

                    <Box sx={{ mt: 3, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Destination:
                        </Typography>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderRadius: 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FolderIcon
                                    sx={{
                                        color:
                                            themeVariant === 'vibrant'
                                                ? getVibrantIconColor('folder', darkMode)
                                                : 'primary.main',
                                        fontSize: '1.75rem',
                                        mr: 1,
                                    }}
                                    color="primary"
                                />
                                <Typography variant="body2" fontWeight="medium" data-testid="folder-destination-path">
                                    {folderDestinationPath === '/' ? 'Home' : folderDestinationPath}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setFolderPathDialogOpen(true)}
                                data-testid="change-destination-button"
                                themeVariant={themeVariant}
                                darkMode={darkMode}
                            >
                                Change
                            </Button>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCreateFolderDialogOpen(false)}
                        data-testid="cancel-folder-button"
                        variant="outlined"
                        color="secondary"
                        themeVariant={themeVariant}
                        darkMode={darkMode}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateFolderSubmit}
                        variant="outlined"
                        color="primary"
                        disabled={!newFolderName.trim() || !!validateItemName(newFolderName, 'folder')}
                        data-testid="create-folder-button"
                        themeVariant={themeVariant}
                        darkMode={darkMode}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <PathSelectorDialog
                open={folderPathDialogOpen}
                onClose={() => setFolderPathDialogOpen(false)}
                onSelect={(path) => {
                    setFolderDestinationPath(path);
                    setFolderPathDialogOpen(false);
                }}
                currentPath={folderDestinationPath}
                title="Select Folder Destination"
                data-testid="path-selector-dialog"
            />
            <SectionManagerDialog
                open={sectionManagerDialogOpen}
                onClose={() => setSectionManagerDialogOpen(false)}
                videoId={selectedFileForSections?.id || selectedFileForSections?.data || selectedFileForSections?._id}
            />

            <FolderHistoryDialog
                open={folderHistoryDialogOpen}
                onClose={() => setFolderHistoryDialogOpen(false)}
                folderId={selectedFolderForHistory?.id}
                folderName={selectedFolderForHistory?.name}
            />

            <VoiceOverDialog
                open={voiceOverDialogOpen}
                onClose={handleVoiceOverDialogClose}
                videoId={selectedVideoForVoiceOver}
                onSuccess={handleVoiceOverSuccess}
                folderPath={currentPath}
            />

            <FolderPermissionsDialog
                open={permissionsDialogOpen}
                onClose={() => setPermissionsDialogOpen(false)}
                folderPath={selectedFolderForPermissions?.path || ''}
                folderName={selectedFolderForPermissions?.name || ''}
            />

            <BulkDownloadDialog
                open={bulkDownloadDialogOpen}
                onClose={() => setBulkDownloadDialogOpen(false)}
                videoFiles={selectedFolderForBulkDownload ? videoFiles : []}
                folderName={selectedFolderForBulkDownload?.name || 'Folder'}
                currentPath={currentPath}
            />
            <AudioFileDetailsDialog
                open={audioFileDetailsDialogOpen}
                onClose={() => {
                    setAudioFileDetailsDialogOpen(false);
                    setSelectedAudioFileDetails(null);
                }}
                audioFile={selectedAudioFileDetails}
            />
            <TextFileDetailsDialog
                open={textFileDetailsDialogOpen}
                onClose={() => {
                    setTextFileDetailsDialogOpen(false);
                    setSelectedTextFileDetails(null);
                }}
                textFile={selectedTextFileDetails}
                onUpdated={() => {
                    loadFolderContents(currentPath);
                }}
            />
            <BookFileDetailsDialog
                open={bookFileDetailsDialogOpen}
                onClose={() => {
                    setBookFileDetailsDialogOpen(false);
                    setSelectedBookFileDetails(null);
                }}
                book={selectedBookFileDetails}
                onUpdated={() => {
                    loadFolderContents(currentPath);
                }}
            />

            {/* Translate text file dialog */}
            <Dialog open={textFileTranslateDialogOpen} onClose={() => setTextFileTranslateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Translate Text Files</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Select files to translate:
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={textFileTranslateFileIds.size === textFiles.length && textFiles.length > 0}
                                indeterminate={textFileTranslateFileIds.size > 0 && textFileTranslateFileIds.size < textFiles.length}
                                onChange={(e) => setTextFileTranslateFileIds(e.target.checked ? new Set(textFiles.map((tf) => tf.data)) : new Set())}
                                size="small"
                            />
                        }
                        label={<Typography variant="body2" fontWeight={600}>Select All</Typography>}
                        sx={{ mb: 0.5 }}
                    />
                    <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                        {textFiles.map((tf) => (
                            <Box key={tf.data} sx={{ px: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={textFileTranslateFileIds.has(tf.data)}
                                            onChange={() => setTextFileTranslateFileIds((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(tf.data)) next.delete(tf.data); else next.add(tf.data);
                                                return next;
                                            })}
                                            size="small"
                                        />
                                    }
                                    label={<Typography variant="body2">{tf.name}</Typography>}
                                    sx={{ width: '100%' }}
                                />
                            </Box>
                        ))}
                    </Box>
                    <TextField
                        select
                        fullWidth
                        label="Target Language"
                        value={textFileTranslateTarget}
                        onChange={(e) => setTextFileTranslateTarget(e.target.value)}
                        SelectProps={{ native: true }}
                    >
                        <option value="" disabled></option>
                        {flatLangOptions.map((lang) => (
                            <option key={lang.value} value={lang.label}>{lang.label}</option>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTextFileTranslateDialogOpen(false)} disabled={textFileTranslating}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleTextFileTranslateSubmit}
                        disabled={textFileTranslateFileIds.size === 0 || !textFileTranslateTarget || textFileTranslating}
                    >
                        {textFileTranslating ? <CircularProgress size={20} /> : `Start Translation (${textFileTranslateFileIds.size})`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Download text files by language dialog */}
            <Dialog open={downloadByLangDialogOpen} onClose={() => setDownloadByLangDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <DownloadIcon color="primary" />
                        <Box>
                            <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>Download Text Files</Typography>
                            <Typography variant="caption" color="text.secondary">Select languages, formats, and files — packaged as a ZIP</Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2, pb: 1 }}>

                    {/* ── Languages ── */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>Languages</Typography>
                        {downloadByLangLanguages.size > 0 && (
                            <Typography variant="caption" color="text.secondary">{downloadByLangLanguages.size} selected</Typography>
                        )}
                    </Box>
                    <Paper variant="outlined" sx={{ mb: 2.5 }}>
                        <Box sx={{ px: 1.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={downloadByLangLanguages.size === flatLangOptions.length && flatLangOptions.length > 0}
                                        indeterminate={downloadByLangLanguages.size > 0 && downloadByLangLanguages.size < flatLangOptions.length}
                                        onChange={(e) => setDownloadByLangLanguages(e.target.checked ? new Set(flatLangOptions.map((l) => l.label)) : new Set())}
                                        size="small"
                                    />
                                }
                                label={<Typography variant="body2" fontWeight={600}>Select All</Typography>}
                            />
                        </Box>
                        <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                            {flatLangOptions.map((lang, idx) => (
                                <Box key={lang.value} sx={{ px: 1.5, borderBottom: idx < flatLangOptions.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={downloadByLangLanguages.has(lang.label)}
                                                onChange={() => setDownloadByLangLanguages((prev) => {
                                                    const next = new Set(prev);
                                                    if (next.has(lang.label)) next.delete(lang.label); else next.add(lang.label);
                                                    return next;
                                                })}
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="body2">{lang.label}</Typography>}
                                        sx={{ width: '100%', my: 0.25 }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Paper>

                    {/* ── Format ── */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>Format</Typography>
                        {downloadByLangFormats.size > 1 && (
                            <Typography variant="caption" color="text.secondary">{downloadByLangFormats.size} selected</Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                        {[
                            { value: 'docx', label: 'Word (.docx)' },
                            { value: 'pdf',  label: 'PDF (.pdf)' },
                            { value: 'txt',  label: 'Plain Text (.txt)' },
                            { value: 'html', label: 'HTML' },
                        ].map((fmt) => (
                            <Chip
                                key={fmt.value}
                                label={fmt.label}
                                onClick={() => setDownloadByLangFormats((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(fmt.value)) next.delete(fmt.value); else next.add(fmt.value);
                                    return next;
                                })}
                                color={downloadByLangFormats.has(fmt.value) ? 'primary' : 'default'}
                                variant={downloadByLangFormats.has(fmt.value) ? 'filled' : 'outlined'}
                                sx={{ cursor: 'pointer', fontWeight: downloadByLangFormats.has(fmt.value) ? 600 : 400 }}
                            />
                        ))}
                    </Box>

                    {/* ── Files ── */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>Files</Typography>
                        <Typography variant="caption" color="text.secondary">{downloadByLangFileIds.size} / {textFiles.length} selected</Typography>
                    </Box>
                    <Paper variant="outlined">
                        <Box sx={{ px: 1.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={downloadByLangFileIds.size === textFiles.length && textFiles.length > 0}
                                        indeterminate={downloadByLangFileIds.size > 0 && downloadByLangFileIds.size < textFiles.length}
                                        onChange={(e) => setDownloadByLangFileIds(e.target.checked ? new Set(textFiles.map((tf) => tf.data)) : new Set())}
                                        size="small"
                                    />
                                }
                                label={<Typography variant="body2" fontWeight={600}>Select All</Typography>}
                            />
                        </Box>
                        <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
                            {textFiles.map((tf, idx) => (
                                <Box key={tf.data} sx={{ px: 1.5, borderBottom: idx < textFiles.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={downloadByLangFileIds.has(tf.data)}
                                                onChange={() => setDownloadByLangFileIds((prev) => {
                                                    const next = new Set(prev);
                                                    if (next.has(tf.data)) next.delete(tf.data); else next.add(tf.data);
                                                    return next;
                                                })}
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="body2">{tf.name}</Typography>}
                                        sx={{ width: '100%', my: 0.25 }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Paper>

                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">
                        {downloadByLangFileIds.size > 0 && downloadByLangLanguages.size > 0 && downloadByLangFormats.size > 0
                            ? `${downloadByLangFileIds.size} file${downloadByLangFileIds.size !== 1 ? 's' : ''} × ${downloadByLangLanguages.size} language${downloadByLangLanguages.size !== 1 ? 's' : ''} × ${downloadByLangFormats.size} format${downloadByLangFormats.size !== 1 ? 's' : ''}`
                            : 'Select languages, formats, and files above'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setDownloadByLangDialogOpen(false)} disabled={downloadByLangLoading}>Cancel</Button>
                        <Button
                            variant="contained"
                            startIcon={downloadByLangLoading ? <CircularProgress size={18} /> : <DownloadIcon />}
                            onClick={handleDownloadByLanguage}
                            disabled={downloadByLangFileIds.size === 0 || downloadByLangLanguages.size === 0 || downloadByLangFormats.size === 0 || downloadByLangLoading}
                        >
                            {downloadByLangLoading ? 'Downloading...' : 'Download'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FolderBrowser;
